// Per-conversation reply drafts for the eBay buyer-messages composer (docs/ebay/MESSAGES_PLAN.md
// §4): `{ text, key }` in localStorage, so a half-written reply — and the idempotency key it was
// already sent under — survives a reload or a switch to another conversation.
//
// Scoped by the eBay account (its id is unique across business profiles) because a conversation
// id only means something inside one mailbox. Every access is wrapped: storage can be disabled
// (private mode), full, or hold something another version wrote — none of those may break the
// composer, which then just keeps its draft in memory only.
const PREFIX = 'wp.ebay.messages.draft';

const storageKey = (accountId, conversationId) => `${PREFIX}.${accountId}.${conversationId}`;

// A draft with no text is not a draft. `key` is the idempotency key the text was last sent
// under (null when it has never been sent, or its key was retired).
export const readReplyDraft = (accountId, conversationId) => {
  if (accountId == null || !conversationId) return null;
  try {
    const raw = localStorage.getItem(storageKey(accountId, conversationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.text !== 'string' || !parsed.text) return null;
    return { text: parsed.text, key: typeof parsed.key === 'string' && parsed.key ? parsed.key : null };
  } catch {
    return null;
  }
};

// Empty text removes the entry rather than storing an empty one.
export const writeReplyDraft = (accountId, conversationId, draft) => {
  if (accountId == null || !conversationId) return;
  try {
    if (!draft?.text) {
      localStorage.removeItem(storageKey(accountId, conversationId));
      return;
    }
    localStorage.setItem(
      storageKey(accountId, conversationId),
      JSON.stringify({ text: draft.text, key: draft.key ?? null }),
    );
  } catch {
    // Storage unavailable or full — the draft lives in memory only.
  }
};

export const removeReplyDraft = (accountId, conversationId) => {
  if (accountId == null || !conversationId) return;
  try {
    localStorage.removeItem(storageKey(accountId, conversationId));
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
};

// A UUIDv4 for a reply's idempotency key. `crypto.randomUUID` only exists in secure contexts
// (https / localhost), so an http dev origin falls back to getRandomValues — never Math.random:
// two replies sharing a key would be treated by the ETL as one and the second silently dropped.
export const newIdempotencyKey = () => {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// eBay's 2,000-character limit (and the ETL's check of it) counts characters, not UTF-16 code
// units — `'😀'.length` is 2, so an emoji-heavy reply would show as over the limit while eBay
// would still take it. The counter and the limit check both use this.
export const replyLength = (text) => (text ? Array.from(text).length : 0);
